import { formatDate as formatDisplayDate } from '@/lib/formatDate';
import {useFormContext} from "react-hook-form";
import {useState, useRef, useId} from "react";
import {Calendar, X} from "lucide-react";
import { addDays, format, isValid, parseISO } from "date-fns";
import { sessionsOn, useClinicHours } from "@/hooks/useClinicHours";
import { Segmented } from "@/components/ui/segmented";

type Unit = "Days" | "Weeks" | "Months";

function addTime(amount: number, unit: Unit): string {
    const d = new Date();
    if (unit === "Days") d.setDate(d.getDate() + amount);
    if (unit === "Weeks") d.setDate(d.getDate() + amount * 7);
    if (unit === "Months") d.setMonth(d.getMonth() + amount);
    return format(d, "yyyy-MM-dd");
}

// "Sat, 04 Oct 2026" — the weekday is what the patient is told
const formatDateDisplay = (dateStr: string): string => {
    const d = parseISO(dateStr);
    return isValid(d) ? format(d, "EEE, ") + formatDisplayDate(d) : formatDisplayDate(dateStr);
};

// showPicker() isn't supported on older Safari/iPadOS — fall back to focus,
// which still opens the native picker on those browsers.
const openDatePicker = (input: HTMLInputElement | null) => {
    if (!input) return;
    if (typeof input.showPicker === "function") input.showPicker();
    else input.focus();
};

// A follow-up is at most two years out; more is a typing slip ("999 months")
const MAX_AMOUNT: Record<Unit, number> = { Days: 365, Weeks: 52, Months: 24 };

export default function NextVisitSection() {
    const {setValue, watch} = useFormContext();
    const selectedDate: string = watch("next_visit")?.date || "";

    const [amount, setAmount] = useState("");
    const [unit, setUnit] = useState<Unit>("Days");
    const dateRef = useRef<HTMLInputElement>(null);

    // A follow-up on a day the clinic is closed (Settings → Clinic hours): say so, and offer the next open day
    const clinicHours = useClinicHours();
    const closedOn = selectedDate && sessionsOn(clinicHours, selectedDate)?.length === 0 ? parseISO(selectedDate) : null;
    const nextOpen = closedOn
        ? [1, 2, 3, 4, 5, 6].map(n => addDays(closedOn, n)).find(d => sessionsOn(clinicHours, d)?.length)
        : undefined;

    const handleAmountChange = (val: string, currentUnit: Unit) => {
        if (parseInt(val) > MAX_AMOUNT[currentUnit]) return;
        setAmount(val);
        const n = parseInt(val);
        if (!n || n <= 0) {
            setValue("next_visit.date", "", {shouldDirty: true});
            return;
        }
        setValue("next_visit.date", addTime(n, currentUnit), {shouldDirty: true});
    };

    const handleUnitChange = (newUnit: Unit) => {
        setUnit(newUnit);
        const n = Math.min(parseInt(amount), MAX_AMOUNT[newUnit]);
        if (n && String(n) !== amount) setAmount(String(n));
        if (n && n > 0) {
            setValue("next_visit.date", addTime(n, newUnit), {shouldDirty: true});
        }
    };

    const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setValue("next_visit.date", e.target.value, {shouldDirty: true});
        setAmount("");
    };

    const handleClear = () => {
        setValue("next_visit.date", "", {shouldDirty: true});
        setAmount("");
    };

    return (
        // As in the mockup: "[5] Days / Weeks / Months → Sat, 04 Oct 2026"; the date opens a picker too
        <div className="flex flex-wrap items-center gap-2.5">
            <input
                type="number"
                min="1"
                max={MAX_AMOUNT[unit]}
                value={amount}
                onChange={(e) => handleAmountChange(e.target.value, unit)}
                aria-label="Follow-up in"
                className="h-8 touch:h-10 w-14 px-2.5 rounded-md border border-field bg-card outline-none font-mono text-sm focus:border-ring focus:ring-2 focus:ring-ring/20 transition-colors [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
            />

            <Segmented<Unit>
                aria-label="Unit"
                options={(["Days", "Weeks", "Months"] as Unit[]).map(u => ({ value: u, label: u }))}
                value={unit}
                onChange={handleUnitChange}
            />

            <div className="relative flex items-center gap-1">
                <button
                    type="button"
                    onClick={() => openDatePicker(dateRef.current)}
                    className="touch-target inline-flex items-center gap-1.5 h-8 px-1 rounded text-sm hover:bg-accent"
                    title="Pick a date"
                >
                    {selectedDate ? (
                        <span className="text-label">→ {formatDateDisplay(selectedDate)}</span>
                    ) : (
                        <span className="inline-flex items-center gap-1.5 text-muted-foreground">
                            <Calendar className="w-3.5 h-3.5" /> or pick a date
                        </span>
                    )}
                </button>

                <input
                    ref={dateRef}
                    type="date"
                    value={selectedDate}
                    onChange={handleDateChange}
                    min={format(new Date(), "yyyy-MM-dd")}
                    className="w-0 h-0 absolute left-0 bottom-0 opacity-0 pointer-events-none"
                    tabIndex={-1}
                />

                {selectedDate && (
                    <button
                        type="button"
                        onClick={handleClear}
                        aria-label="Clear follow-up date"
                        className="touch-target p-1 rounded-full text-muted-foreground hover:bg-accent hover:text-foreground"
                    >
                        <X className="w-3 h-3"/>
                    </button>
                )}
            </div>

            {closedOn && (
                <span className="text-xs text-warning">
                    Clinic closed on {format(closedOn, "EEEE")}s
                    {nextOpen && (
                        <button
                            type="button"
                            onClick={() => { setValue("next_visit.date", format(nextOpen, "yyyy-MM-dd"), {shouldDirty: true}); setAmount(""); }}
                            className="ml-1.5 font-medium text-primary hover:underline underline-offset-2"
                        >
                            Move to {format(nextOpen, "EEE, d MMM")}
                        </button>
                    )}
                </span>
            )}
        </div>
    );
}